import axios from 'axios';
import type { AxiosInstance, AxiosError } from 'axios';

// 使用一个全局变量存储 message 实例
let messageApi: any = null;

export const setMessageApi = (api: any) => {
  messageApi = api;
};

// 开发环境使用代理，生产环境使用环境变量
const API_BASE_URL = import.meta.env.PROD
  ? (import.meta.env.VITE_API_BASE_URL || '')
  : '';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // 如果是FormData，删除Content-Type让浏览器自动设置boundary
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        // silent: true 时跳过全局错误提示，由调用方自行处理
        const silent = (error.config as any)?.silent === true;

        if (error.response) {
          const { status, data } = error.response;

          // 安全地提取错误消息字符串
          const getErrorMessage = (d: any): string => {
            if (typeof d?.error === 'string') return d.error;
            if (typeof d?.error?.message === 'string') return d.error.message;
            if (typeof d?.message === 'string') return d.message;
            return '请求失败';
          };

          const errorMessage = getErrorMessage(data);

          if (!silent) {
            switch (status) {
              case 400:
                // 400 业务错误由调用方处理，不在拦截器显示
                break;
              case 401:
                messageApi?.error('未授权，请登录');
                window.location.href = '/auth/saml/login';
                break;
              case 403:
                // 403 由各调用方自行处理（部分接口的 403 是预期行为，如非审批人访问审批接口）
                break;
              case 404:
                messageApi?.error('请求的资源不存在');
                break;
              case 500:
                messageApi?.error(errorMessage || '服务器错误');
                break;
              default:
                messageApi?.error(errorMessage);
            }
          }
        } else if (error.request) {
          if (!silent) messageApi?.error('网络错误，请检查网络连接');
        } else {
          if (!silent) messageApi?.error('请求配置错误');
        }

        return Promise.reject(error);
      }
    );
  }

  get<T = any>(url: string, configOrParams?: any) {
    // 已经是完整 axios config 对象（含 params/responseType/headers 等），直接透传
    if (configOrParams && (configOrParams.params || configOrParams.responseType || configOrParams.headers || configOrParams.timeout)) {
      return this.client.get<T>(url, configOrParams);
    }
    // 裸对象视为 params
    return this.client.get<T>(url, { params: configOrParams });
  }

  post<T = any>(url: string, data?: any, config?: any) {
    return this.client.post<T>(url, data, config);
  }

  put<T = any>(url: string, data?: any) {
    return this.client.put<T>(url, data);
  }

  patch<T = any>(url: string, data?: any) {
    return this.client.patch<T>(url, data);
  }

  delete<T = any>(url: string, config?: any) {
    return this.client.delete<T>(url, config);
  }

  head<T = any>(url: string, config?: any) {
    return this.client.head<T>(url, config);
  }
}

export default new ApiService();